import React, { useState, useEffect } from 'react';
import { collection, getDocs, query, where } from 'firebase/firestore';
import { db } from '../firebase';

const PaginatedList = ({ data, title, renderItem, emptyMessage }) => {
    const [displayCount, setDisplayCount] = useState(15);
    const displayedData = data.slice(0, displayCount);

    // Reset display count if data changes
    useEffect(() => {
        setDisplayCount(15);
    }, [data]);

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            {title && <h3 style={{ fontSize: '16px', color: '#555', marginBottom: '15px' }}>{title}</h3>}
            {data.length === 0 ? (
                typeof emptyMessage === 'string' ? <p style={styles.emptyText}>{emptyMessage}</p> : emptyMessage
            ) : (
                <div style={styles.listContainer}>
                    {displayedData.map((item, index) => renderItem(item, index))}
                </div>
            )}
            
            {data.length > 0 && (
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '10px' }}>
                    <span style={{ fontSize: '14px', color: '#6b7280', fontWeight: '500' }}>
                        Showing {displayedData.length} of {data.length} records
                    </span>
                    {displayCount < data.length && (
                        <button 
                            onClick={() => setDisplayCount(prev => prev + 15)}
                            style={{ 
                                padding: '8px 16px', 
                                backgroundColor: '#f1f5f9', 
                                color: '#334155',
                                border: '1px solid #e2e8f0', 
                                borderRadius: '6px', 
                                cursor: 'pointer', 
                                fontWeight: '600',
                                transition: 'background-color 0.2s'
                            }}
                            onMouseOver={(e) => e.target.style.backgroundColor = '#e2e8f0'}
                            onMouseOut={(e) => e.target.style.backgroundColor = '#f1f5f9'}
                        >
                            Load More
                        </button>
                    )}
                </div>
            )}
        </div>
    );
};

const EmployeePersonalAdminModule = ({ employee, serverId, canEdit }) => {
    const [activeTab, setActiveTab] = useState('nextOfKin');
    
    // Data states
    const [deviceLogs, setDeviceLogs] = useState([]);
    const [concerns, setConcerns] = useState([]);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (!employee?.username || !serverId) return;

        const fetchData = async () => {
            setLoading(true);
            try {
                if (activeTab === 'sharedAccess') {
                    // Fetch device access logs
                    const accessRef = collection(db, 'servers', serverId, 'employees', employee.username, 'deviceAccess');
                    const snapshot = await getDocs(accessRef);
                    let devices = [];
                    snapshot.forEach(doc => {
                        devices.push({ id: doc.id, ...doc.data() });
                    });
                    setDeviceLogs(devices);
                } else if (activeTab === 'concerns') {
                    // Fetch concerns where the user is involved
                    // Try fetching from a top level concerns collection
                    const concernsRef = collection(db, 'servers', serverId, 'concerns');
                    // We'll fetch all and filter client side if the index is missing, but try a basic query
                    // Assuming 'userID' or 'staffID' field exists based on Flutter's parameter passing
                    const q = query(concernsRef, where('userID', '==', employee.username));
                    
                    try {
                        const snapshot = await getDocs(q);
                        let fetchedConcerns = [];
                        snapshot.forEach(doc => {
                            fetchedConcerns.push({ id: doc.id, ...doc.data() });
                        });
                        setConcerns(fetchedConcerns);
                    } catch (err) {
                        console.warn("Index might be missing for concerns query. Falling back to fetching all and filtering.", err);
                        const fallbackSnap = await getDocs(concernsRef);
                        let fallbackConcerns = [];
                        fallbackSnap.forEach(doc => {
                            const data = doc.data();
                            if (data.userID === employee.username || data.staffID === employee.username) {
                                fallbackConcerns.push({ id: doc.id, ...data });
                            }
                        });
                        setConcerns(fallbackConcerns);
                    }
                }
            } catch (error) {
                console.error("Error fetching data:", error);
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [activeTab, employee?.username, serverId]);

    const renderTabs = () => (
        <div style={styles.tabContainer}>
            <button
                style={{ ...styles.tabButton, ...(activeTab === 'nextOfKin' ? styles.activeTabButton : {}) }}
                onClick={() => setActiveTab('nextOfKin')}
            >
                Next of Kin
            </button>
            <button
                style={{ ...styles.tabButton, ...(activeTab === 'sharedAccess' ? styles.activeTabButton : {}) }}
                onClick={() => setActiveTab('sharedAccess')}
            >
                Shared Access
            </button>
            <button
                style={{ ...styles.tabButton, ...(activeTab === 'concerns' ? styles.activeTabButton : {}) }}
                onClick={() => setActiveTab('concerns')}
            >
                Concerns Management
            </button>
        </div>
    );

    const renderNextOfKin = () => {
        const nok = employee?.nextOfKin || {};
        
        return (
            <div style={styles.contentSection}>
                <div style={styles.card}>
                    <h3 style={styles.cardTitle}>Next of Kin Details</h3>
                    <div style={styles.grid}>
                        <div style={styles.field}>
                            <span style={styles.label}>Name</span>
                            <span style={styles.value}>{nok.name || 'Not provided'}</span>
                        </div>
                        <div style={styles.field}>
                            <span style={styles.label}>Relationship</span>
                            <span style={styles.value}>{nok.relation || 'Not provided'}</span>
                        </div>
                        <div style={styles.field}>
                            <span style={styles.label}>Email</span>
                            <span style={styles.value}>{nok.email || 'Not provided'}</span>
                        </div>
                        <div style={styles.field}>
                            <span style={styles.label}>Phone</span>
                            <span style={styles.value}>{nok.phone || 'Not provided'}</span>
                        </div>
                        <div style={styles.field}>
                            <span style={styles.label}>Address</span>
                            <span style={styles.value}>{nok.address || 'Not provided'}</span>
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    const renderSharedAccess = () => {
        if (loading) return <p style={styles.loadingText}>Loading device access logs...</p>;
        
        return (
            <div style={styles.contentSection}>
                <PaginatedList
                    data={deviceLogs}
                    emptyMessage="No shared access devices found."
                    renderItem={(device) => (
                        <div key={device.id} style={styles.listCard}>
                            <div style={styles.listCardHeader}>
                                <h4 style={styles.listCardTitle}>{device.deviceModel || device.id || 'Unknown Device'}</h4>
                            </div>
                            <div style={styles.listCardBody}>
                                <div style={styles.detailRow}>
                                    <span style={styles.detailLabel}>Last Access:</span>
                                    <span style={styles.detailValue}>
                                        {device.lastAccess ? (device.lastAccess.toDate ? device.lastAccess.toDate().toLocaleString() : device.lastAccess) : 'N/A'}
                                    </span>
                                </div>
                            </div>
                        </div>
                    )}
                />
            </div>
        );
    };

    const renderConcerns = () => {
        if (loading) return <p style={styles.loadingText}>Loading concerns...</p>;

        return (
            <div style={styles.contentSection}>
                <PaginatedList
                    data={concerns}
                    emptyMessage="No concerns found for this employee."
                    renderItem={(concern) => (
                        <div key={concern.id} style={styles.listCard}>
                            <div style={styles.listCardHeader}>
                                <h4 style={styles.listCardTitle}>{concern.title || concern.category || 'Concern Log'}</h4>
                                <span style={{ ...styles.badge, backgroundColor: concern.status === 'Resolved' ? '#10b981' : '#ef4444' }}>
                                    {concern.status || 'Open'}
                                </span>
                            </div>
                            <div style={styles.listCardBody}>
                                <p style={styles.concernDescription}>{concern.description || concern.details || 'No details provided.'}</p>
                                <div style={styles.detailRow}>
                                    <span style={styles.detailLabel}>Date Raised:</span>
                                    <span style={styles.detailValue}>
                                        {concern.createdAt ? (concern.createdAt.toDate ? concern.createdAt.toDate().toLocaleDateString() : concern.createdAt) : 'N/A'}
                                    </span>
                                </div>
                            </div>
                        </div>
                    )}
                />
            </div>
        );
    };

    return (
        <div style={styles.container}>
            {renderTabs()}
            <div style={styles.tabContent}>
                {activeTab === 'nextOfKin' && renderNextOfKin()}
                {activeTab === 'sharedAccess' && renderSharedAccess()}
                {activeTab === 'concerns' && renderConcerns()}
            </div>
        </div>
    );
};

const styles = {
    container: {
        backgroundColor: '#ffffff',
        borderRadius: '12px',
        boxShadow: '0 4px 6px rgba(0, 0, 0, 0.05)',
        border: '1px solid #f3f4f6',
        overflow: 'hidden',
        marginTop: '20px'
    },
    tabContainer: {
        display: 'flex',
        backgroundColor: '#f8fafc',
        borderBottom: '1px solid #e2e8f0',
        padding: '0 10px',
    },
    tabButton: {
        padding: '16px 20px',
        fontSize: '14px',
        fontWeight: '600',
        color: '#64748b',
        backgroundColor: 'transparent',
        border: 'none',
        borderBottom: '3px solid transparent',
        cursor: 'pointer',
        transition: 'all 0.2s',
    },
    activeTabButton: {
        color: '#0f172a',
        borderBottomColor: '#3b82f6',
    },
    tabContent: {
        padding: '24px',
        minHeight: '300px',
    },
    contentSection: {
        display: 'flex',
        flexDirection: 'column',
        gap: '20px',
    },
    card: {
        padding: '24px',
        backgroundColor: '#f8fafc',
        border: '1px solid #e2e8f0',
        borderRadius: '10px',
    },
    cardTitle: {
        margin: '0 0 20px 0',
        fontSize: '18px',
        fontWeight: '700',
        color: '#0f172a',
        borderBottom: '2px solid #e2e8f0',
        paddingBottom: '10px'
    },
    grid: {
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
        gap: '24px',
    },
    field: {
        display: 'flex',
        flexDirection: 'column',
        gap: '8px',
    },
    label: {
        fontSize: '13px',
        fontWeight: '600',
        color: '#64748b',
        textTransform: 'uppercase',
        letterSpacing: '0.5px'
    },
    value: {
        fontSize: '15px',
        fontWeight: '600',
        color: '#1e293b',
        wordBreak: 'break-word',
    },
    listContainer: {
        display: 'flex',
        flexDirection: 'column',
        gap: '16px',
    },
    listCard: {
        padding: '20px',
        backgroundColor: '#ffffff',
        border: '1px solid #e2e8f0',
        borderRadius: '10px',
        display: 'flex',
        flexDirection: 'column',
        gap: '12px',
        transition: 'box-shadow 0.2s',
    },
    listCardHeader: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        borderBottom: '1px solid #f1f5f9',
        paddingBottom: '12px',
    },
    listCardTitle: {
        margin: 0,
        fontSize: '16px',
        fontWeight: '600',
        color: '#1e293b',
    },
    badge: {
        padding: '4px 10px',
        borderRadius: '20px',
        fontSize: '12px',
        fontWeight: '700',
        color: '#ffffff',
    },
    listCardBody: {
        display: 'flex',
        flexDirection: 'column',
        gap: '8px',
    },
    concernDescription: {
        margin: '0 0 10px 0',
        fontSize: '14px',
        color: '#475569',
        lineHeight: '1.5'
    },
    detailRow: {
        display: 'flex',
        justifyContent: 'space-between',
        fontSize: '14px',
    },
    detailLabel: {
        color: '#64748b',
        fontWeight: '500',
    },
    detailValue: {
        color: '#0f172a',
        fontWeight: '600',
    },
    loadingText: {
        textAlign: 'center',
        color: '#64748b',
        padding: '40px 0',
        fontSize: '15px',
    },
    emptyText: {
        textAlign: 'center',
        color: '#94a3b8',
        padding: '40px 0',
        fontSize: '15px',
        fontStyle: 'italic',
        border: '1px dashed #e2e8f0',
        borderRadius: '8px',
        backgroundColor: '#f8fafc'
    }
};

export default EmployeePersonalAdminModule;